// On-screen button control surface for STARCODE.
//
// Why this exists: several of the original NEU.pde keyboard shortcuts (the number row, "ß",
// held A/S, Cmd/Ctrl+Shift combos, ...) collide with browser/OS-reserved shortcuts and layout-
// dependent key codes, so they don't reliably reach the page in every browser/keyboard layout.
// Small on-screen buttons sidestep all of that entirely - a click is a click, no interference.
//
// Keyboard shortcuts in keybinds.js are left in place as a bonus for anyone who wants them; this
// module is the primary, interference-free way to drive every single effect.

import { state, toggleEffect, resetEffects, setPermanentColor } from "./state.js";
import { MIDI_TARGETS } from "./midi.js";
import { t, getLang } from "./i18n.js";

let captureCameraState = () => null;
let applyCameraState = () => {};
let applyParticleCount = () => {};

export function exportPresetJSON() {
  const userList = (function() {
    try {
      return JSON.parse(localStorage.getItem("starcode.userPresets.v1") || "[]");
    } catch {
      return [];
    }
  })();

  const currentState = {};
  const skipKeys = new Set([
    "midiConnected", "audioReady", "lastToggledEffect", "loadedObjName",
    "explodeTime", "shockwaveTime", "shockwaveStrength", "midiValue",
    "viewportTopMode"
  ]);

  for (const key in state) {
    if (typeof state[key] !== "function" && !skipKeys.has(key)) {
      currentState[key] = state[key];
    }
  }

  const exportData = {
    app: "STARCODE",
    version: "2.0",
    date: new Date().toISOString(),
    state: currentState,
    camera: captureCameraState(),
    userPresets: userList
  };

  const jsonStr = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const nowStr = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  a.download = `starcode-pack-${nowStr}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function importPresetJSON(jsonText) {
  try {
    const data = JSON.parse(jsonText);
    
    // Check if this is a collection bundle containing userPresets
    if (data.userPresets && Array.isArray(data.userPresets)) {
      const existing = (function() {
        try {
          return JSON.parse(localStorage.getItem("starcode.userPresets.v1") || "[]");
        } catch {
          return [];
        }
      })();

      const merged = [...existing];
      for (const up of data.userPresets) {
        if (up && up.name && up.state) {
          // Avoid exact name duplicates
          if (!merged.some(e => e.name === up.name)) {
            merged.push(up);
          }
        }
      }
      localStorage.setItem("starcode.userPresets.v1", JSON.stringify(merged));
    }

    const stateObj = data.state || data;
    if (stateObj && typeof stateObj === "object") {
      for (const key in stateObj) {
        if (key !== "viewportTopMode" && key in state && typeof state[key] !== "function") {
          state[key] = stateObj[key];
        }
      }
      if (stateObj.outputWidth || stateObj.outputHeight || stateObj.outputPreset) {
        window.dispatchEvent(new Event("resize"));
      }
      if (stateObj.particleCount) applyParticleCount(stateObj.particleCount);
    }
    if (data.camera) applyCameraState(data.camera);

    return true;
  } catch (err) {
    alert("Fehler beim Laden der Datei: " + err.message);
    return false;
  }
}

export const FACTORY_PRESETS = [
  {
    id: "standard",
    label: "🌟 Standard (Swarm & Flow)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: false, rotationSpeed: 0.35, rotationAudioReact: false,
      bloomAmount: 0.25, speedScale: 0.40, masterIntensity: 1.0, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "solar_blaze",
    label: "☀️ Solar Blaze (SUN & Korona)",
    state: {
      sunOn: true, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.25, rotationAudioReact: true,
      bloomAmount: 0.55, speedScale: 0.45, masterIntensity: 1.1, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "gargantua",
    label: "🌀 Gargantua (Black Hole)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: true, neutronStarOn: false, sphereOn: false,
      bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.30, rotationAudioReact: false,
      bloomAmount: 0.60, speedScale: 0.40, masterIntensity: 1.15, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "pulsar_jet",
    label: "⚡ Pulsar Matrix (Neutron Star)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: true, sphereOn: false,
      bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: true, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.40, rotationAudioReact: true,
      bloomAmount: 0.50, speedScale: 0.45, masterIntensity: 1.2, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "synapse_bio",
    label: "✨ Synaptic Deep-Bio (Neural Flash)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      bioluminescenceOn: false, bioSynapseOn: true, bioSynapseIntensity: 1.25, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.35, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.25, rotationAudioReact: true,
      bloomAmount: 0.50, speedScale: 0.40, masterIntensity: 1.15, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "galaxy_04",
    label: "🌌 Galaxy 04 (Barred Starburst)",
    state: {
      galaxy04On: true, sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      starSkyOn: true, bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.35, rotationAudioReact: true,
      bloomAmount: 0.35, speedScale: 0.42, masterIntensity: 1.15, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "ring_nebula",
    label: "🪐 Ring-Nebula (M57 Ionization)",
    state: {
      ringNebulaOn: true, sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      starSkyOn: true, bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.25, rotationAudioReact: false,
      bloomAmount: 0.45, speedScale: 0.38, masterIntensity: 1.1, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "deep_galaxy",
    label: "🌌 Galaxy 01 (Kepler Spiral)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      galaxySpiralOn: true, starSkyOn: true, bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false,
      chromaWaveOn: false, beatFlashOn: false, permanentColor: 0, dofBlur: 0.0, dofInvert: false, dofAudioReact: false,
      rotationSpeed: 0.35, rotationAudioReact: true, bloomAmount: 0.45, speedScale: 0.40, masterIntensity: 1.0,
      hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "ocean_bio",
    label: "🌊 Ocean Bio (Bioluminescence)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      bioluminescenceOn: true, bioSynapseOn: false, chromaWaveOn: true, causticsOn: true, beatFlashOn: false, permanentColor: 0,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.25, rotationAudioReact: true,
      bloomAmount: 0.45, speedScale: 0.36, masterIntensity: 1.0, bioIntensity: 1.1, hueShift: 0.0, colorWheelActive: false,
    }
  },
  {
    id: "film_noir",
    label: "🎬 Film Noir (B&W Bokeh)",
    state: {
      sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, sphereOn: false,
      bioluminescenceOn: false, bioSynapseOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 3,
      dofBlur: 0.0, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.15, rotationAudioReact: false,
      bloomAmount: 0.35, speedScale: 0.35, masterIntensity: 1.0, hueShift: 0.0, colorWheelActive: false,
    }
  },
];

export function applyPreset(presetData, cameraData = null) {
  const viewportTopMode = state.viewportTopMode;
  resetEffects();
  Object.assign(state, presetData);
  state.viewportTopMode = viewportTopMode;
  if (presetData.particleCount) applyParticleCount(presetData.particleCount);
  if (cameraData) applyCameraState(cameraData);
}

export function loadUserPresets() {
  try {
    return JSON.parse(localStorage.getItem("starcode.userPresets.v1") || "[]");
  } catch {
    return [];
  }
}

export function saveUserPresets(arr) {
  localStorage.setItem("starcode.userPresets.v1", JSON.stringify(arr));
}

function getCategories() {
  return [
    {
      catId: "catAttract",
      title: t("catPhysics"),
      sections: [
        {
          title: t("catPhysicsSec1"),
          items: [
            { kind: "held", label: t("btnAttractorNeg"), field: "negAttractorHeld" },
            { kind: "held", label: t("btnAttractorPos"), field: "posAttractorHeld" },
            { kind: "toggle", label: t("btnGravity"), name: "gravity", field: "gravityOn" },
            { kind: "toggle", label: t("btnGravityWells"), name: "wellsOn", field: "wellsOn" },
            { kind: "toggle", label: t("btnChaosBoost"), name: "chaosBoostOn", field: "chaosBoostOn" },
            { kind: "toggle", label: t("btnSlowMotion"), name: "slowMotion", field: "slowMotion" },
            { kind: "oneshot", label: t("btnShockwave"), action: "shockwave", classExtra: "shockwaveBtn" },
            { kind: "toggle", label: t("btnShockwaveAudio"), name: "shockwaveAudioOn", field: "shockwaveAudioOn", classExtra: "shockwaveAudioBtn", title: t("tipShockwaveAudio") },
            { kind: "oneshot", label: t("btnExplosion"), action: "explosion" },
            { kind: "checkboxToggle", label: t("btnExplosionAudio"), name: "explosionAudioOn", field: "explosionAudioOn", title: t("tipExplosionAudio") },
            { kind: "oneshot", label: t("btnRbBurst"), action: "rbBurst" },
          ],
        },
        {
          title: "Beats, Shockwaves & Wellen",
          items: [
            { kind: "toggle", label: t("btnBeatPulse"), name: "beatPulseOn", field: "beatPulseOn" },
            { kind: "toggle", label: t("btnBeatFlash"), name: "beatFlashOn", field: "beatFlashOn" },
            { kind: "toggle", label: t("btnStrobeField"), name: "strobeFieldOn", field: "strobeFieldOn" },
            { kind: "toggle", label: t("btnLiquidRipple"), name: "liquidRippleOn", field: "liquidRippleOn" },
            { kind: "toggle", label: t("btnRipple"), name: "ripple", field: "rippleOn" },
            { kind: "toggle", label: t("btnCaustics"), name: "causticsOn", field: "causticsOn" },
            { kind: "toggle", label: t("btnComplex"), name: "complexOn", field: "complexOn" },
            { kind: "toggle", label: t("btnBandExplode"), name: "bandExplodeOn", field: "bandExplodeOn" },
            { kind: "toggle", label: t("btnShimmer"), name: "shimmerOn", field: "shimmerOn" },
            { kind: "toggle", label: t("btnAntimatterCollision"), name: "antimatterCollisionOn", field: "antimatterCollisionOn" },
            { kind: "toggle", label: t("btnCryoLattice"), name: "cryoLatticeOn", field: "cryoLatticeOn" },
            { kind: "toggle", label: t("btnHyperdriveWarp"), name: "hyperdriveWarpOn", field: "hyperdriveWarpOn" },
          ],
        },
      ],
    },
    {
      catId: "catShapes",
      title: t("catFormz"),
      sections: [
        {
          title: t("catFormzSec1"),
          items: [
            { kind: "toggle", label: t("btnBlackHole"), name: "blackHoleOn", field: "blackHoleOn" },
            { kind: "toggle", label: t("btnNeutronStar"), name: "neutronStarOn", field: "neutronStarOn" },
            { kind: "toggle", label: t("btnSun"), name: "sunOn", field: "sunOn" },
            { kind: "toggle", label: t("btnGalaxy01"), name: "galaxySpiralOn", field: "galaxySpiralOn" },
            { kind: "toggle", label: t("btnGalaxy02"), name: "galaxyDiscOn", field: "galaxyDiscOn" },
            { kind: "toggle", label: t("btnGalaxy03"), name: "cosmicGalaxyOn", field: "cosmicGalaxyOn" },
            { kind: "toggle", label: t("btnGalaxy04"), name: "galaxy04On", field: "galaxy04On" },
            { kind: "toggle", label: t("btnRingNebula"), name: "ringNebulaOn", field: "ringNebulaOn" },
            { kind: "toggle", label: t("btnSphere"), name: "sphereOn", field: "sphereOn" },
            { kind: "toggle", label: t("btnCrystal"), name: "crystalOn", field: "crystalOn" },
            { kind: "toggle", label: t("btnAudioRing"), name: "ring", field: "ringOn" },
          ],
        },
        {
          title: t("catFormzSec2"),
          items: [
            { kind: "toggle", label: t("btnFireflyRiver"), name: "fireflyRiverOn", field: "fireflyRiverOn" },
            { kind: "toggle", label: t("btnHexMatrix"), name: "hexMatrixOn", field: "hexMatrixOn" },
            { kind: "toggle", label: t("btnFibonacciGrid"), name: "fibonacciGridOn", field: "fibonacciGridOn" },
            { kind: "toggle", label: t("btnBioCell"), name: "bioCellOn", field: "bioCellOn" },
            { kind: "toggle", label: t("btnVortex"), name: "vortexOn", field: "vortexOn" },
            { kind: "toggle", label: t("btnPortal"), name: "portalOn", field: "portalOn" },
            { kind: "toggle", label: t("btnTunnel"), name: "tunnelOn", field: "tunnelOn" },
            { kind: "toggle", label: t("btnHelix"), name: "helixOn", field: "helixOn" },
            { kind: "toggle", label: t("btnFractal"), name: "fractalOn", field: "fractalOn" },
            { kind: "toggle", label: t("btnGridPulse"), name: "gridPulseOn", field: "gridPulseOn" },
            { kind: "toggle", label: t("btnPattern"), name: "patternOn", field: "patternOn" },
            { kind: "toggle", label: t("btnDistortion"), name: "distortionOn", field: "distortionOn" },
            { kind: "toggle", label: t("btnSwarm"), name: "swarmOn", field: "swarmOn" },
            { kind: "toggle", label: t("btnStreams"), name: "streamsOn", field: "streamsOn" },
          ],
        },
      ],
    },
    {
      catId: "catAudio",
      title: t("catAudio"),
      sections: [
        {
          title: t("catAudioSec1"),
          items: [
            {
              kind: "pairedToggleSlider",
              toggle: { label: t("btnBioluminescence"), name: "bioluminescenceOn", field: "bioluminescenceOn" },
              slider: { key: "bioIntensity", field: "bioIntensity", label: t("sliderBioIntensity"), min: 0, max: 2, step: 0.01 },
            },
            {
              kind: "pairedToggleSlider",
              toggle: { label: t("btnBioSynapse"), name: "bioSynapseOn", field: "bioSynapseOn", classExtra: "bioSynapseBtn" },
              slider: { key: "bioSynapseIntensity", field: "bioSynapseIntensity", label: t("sliderBioSynapse"), min: 0, max: 2, step: 0.01 },
            },
            { kind: "toggle", label: t("btnElectro"), name: "electroOn", field: "electroOn", classExtra: "electroBtn" },
            { kind: "toggle", label: t("btnSpectrumColor"), name: "spectrumColorOn", field: "spectrumColorOn" },
            { kind: "toggle", label: t("btnChromaWave"), name: "chromaWaveOn", field: "chromaWaveOn" },
            { kind: "toggle", label: t("btnStarSky"), name: "starSkyOn", field: "starSkyOn" },
            { kind: "toggle", label: t("btnColorInvert"), name: "colorInvertOn", field: "colorInvertOn" },
          ],
        },
        {
          title: "VJ Live & Performance Tools",
          items: [
            { kind: "tapBpm", label: "🥁 Tap BPM" },
            { kind: "blackout", label: "⬛ Blackout Killswitch" },
            { kind: "toggle", label: t("btnScaleFreq"), name: "scalePulseOn", field: "scalePulseOn" },
            { kind: "toggle", label: t("btnAudioSize"), name: "audioSizeOn", field: "audioSizeOn" },
            { kind: "toggle", label: t("btnBreath"), name: "breathOn", field: "breathOn" },
            { kind: "toggle", label: t("btnSpectralCathedral"), name: "spectralCathedralOn", field: "spectralCathedralOn" },
            { kind: "toggle", label: t("btnPulseBloom"), name: "pulseBloomOn", field: "pulseBloomOn" },
            { kind: "toggle", label: t("btnShapeChange"), name: "shapeChangeOn", field: "shapeChangeOn" },
            { kind: "toggle", label: t("btnInkBleed"), name: "inkBleedOn", field: "inkBleedOn" },
          ],
        },
      ],
    },
    {
      catId: "catLive",
      title: "Live, 3D & Kamera",
      sections: [
        {
          title: "Kamera & AutoCam",
          items: [
            { kind: "toggle", label: t("btnCameraFollow"), name: "cameraFollowOn", field: "cameraFollowOn", classExtra: "followBtn" },
            { kind: "toggle", label: t("btnAutoCam"), name: "autoCamOn", field: "autoCamOn", classExtra: "autoCamBtn" },
            { kind: "sliderInline", key: "autoCamDistance", field: "autoCamDistance", label: "AutoCam Distanz", min: 50, max: 3000, step: 10 },
            { kind: "sliderInline", key: "autoCamSpeed", field: "autoCamSpeed", label: "AutoCam Speed", min: 0.1, max: 3.0, step: 0.05 },
            { kind: "cameraFlightSelect" },
          ],
        },
        {
          title: t("catLiveSec2"),
          items: [
            { kind: "morph", label: t("btnStarcodeMorph") },
            { kind: "brandMorph", label: t("btnBrandMorph"), classExtra: "brandBtn" },
            { kind: "objMorph", label: t("btnObjMorph") },
            { kind: "toggle", label: t("btnObjDef1"), name: "objDef1On", field: "objDef1On", classExtra: "objDefBtn" },
            { kind: "toggle", label: t("btnObjDef2"), name: "objDef2On", field: "objDef2On", classExtra: "objDefBtn" },
            { kind: "toggle", label: t("btnObjDef3"), name: "objDef3On", field: "objDef3On", classExtra: "objDefBtn" },
            { kind: "customTextMorph" },
            { kind: "loadObj", label: t("btnLoadObj"), classExtra: "loadObjBtn" },
            { kind: "toggle", label: t("btnWrapMode"), name: "wrapModeOn", field: "wrapModeOn" },
            { kind: "permanentColor", label: t("btnBluePerm"), mode: 1 },
            { kind: "permanentColor", label: t("btnRedPerm"), mode: 2 },
            { kind: "permanentColor", label: t("btnBwPerm"), mode: 3, classExtra: "bwBtn" },
          ],
        },
      ],
    },
    {
      catId: "catEssentials",
      title: t("catEssentials"),
      sections: [
        {
          title: t("catEssentialsSec1"),
          items: [
            { kind: "fullscreen", label: t("btnFullscreen") },
            { kind: "liveMode", label: t("btnLiveMode") },
            { kind: "toggleMute", label: t("btnMuteRender"), name: "muteLocalRender", field: "muteLocalRender", classExtra: "muteBtn" },
          ],
        },
        {
          title: t("catEssentialsSec2"),
          items: [
            { kind: "toggle", label: t("btnAutoMix"), name: "autoMixOn", field: "autoMixOn" },
            { kind: "oneshot", label: "↺ RESET ALL", action: "reset", classExtra: "resetBtnBig" },
          ],
        },
        {
          title: t("boxPresets"),
          items: [
            { kind: "presetsCard" },
          ],
        },
      ],
    },
  ];
}

// Global performance parameters exposed as sliders. Every one of these is also MIDI-assignable
function getSliders() {
  return [
    // --- Group 1: Master, Scale & Color ---
    { key: "master", field: "masterIntensity", label: t("sliderMaster"), min: 0, max: 2, step: 0.01 },
    { key: "size", field: "sizeBoost", label: t("sliderSize"), min: 0.1, max: 3, step: 0.01 },
    { key: "speed", field: "speedScale", label: t("sliderSpeed"), min: 0.05, max: 2, step: 0.01 },

    // --- Group 2: Camera & Rotation ---
    { key: "rotation", field: "rotationSpeed", label: t("sliderRotation"), min: 0, max: 3, step: 0.02 },

    // --- Group 3: Optics & Bloom ---
    { key: "bloom", field: "bloomAmount", label: t("sliderBloom"), min: 0, max: 3, step: 0.01 },

    // --- Group 4: Audio Reactivity & VJ Mix ---
    { key: "gain", field: "audioGain", label: t("sliderAudioGain"), min: 0, max: 3, step: 0.01 },
    { key: "autoMixIntensity", field: "autoMixIntensity", label: t("sliderAutoMixTempo"), min: 0, max: 1, step: 0.01 },
  ];
}

export function installControlBar(options) {
  const {
    particleSystem,
    audio,
    onMorphToggle,
    onLiveModeToggle,
    onFullscreenToggle,
    onResetCamera,
    onOpenOutput,
    onPipToggle,
    onOneshot,
    onObjLoaded,
    onCaptureCamera,
    onApplyCamera,
    onSetParticleCount,
    midi,
  } = options;

  captureCameraState = onCaptureCamera || captureCameraState;
  applyCameraState = onApplyCamera || applyCameraState;
  applyParticleCount = onSetParticleCount || applyParticleCount;

  const container = document.getElementById("controlBar");
  if (!container) return () => {};

  let trackedButtons = [];
  let trackedSliders = [];
  let trackedLearnButtons = [];
  let trackedCategories = [];
  let inputW = null;
  let inputH = null;
  let formatSelect = null;
  let thumb = null;
  let colorStatus = null;
  let colorResetBtn = null;
  let audioWaveCanvas = null;
  let audioWaveCtx = null;

  function renderBar() {
    container.innerHTML = "";
    trackedButtons = [];
    trackedSliders = [];
    trackedLearnButtons = [];
    trackedCategories = [];

    const CATEGORIES = getCategories();
    const SLIDERS = getSliders();

    function makeCollapsible(box, headerEl, storageKey, defaultCollapsed = false) {
      headerEl.classList.add("collapsibleHeader");
      headerEl.title = t("tipCollapse");
      const stored = localStorage.getItem(storageKey);
      let collapsed = stored === null ? defaultCollapsed : stored === "1";
      const apply = () => {
        box.classList.toggle("collapsed", collapsed);
        headerEl.dataset.chevron = collapsed ? "▸" : "▾";
      };
      apply();
      headerEl.addEventListener("click", () => {
        collapsed = !collapsed;
        localStorage.setItem(storageKey, collapsed ? "1" : "0");
        apply();
        window.dispatchEvent(new Event("resize"));
      });
    }

    // --- Category Grid: side-by-side grouped boxes ---
    const grid = document.createElement("div");
    grid.className = "categoryGrid";

    for (const cat of CATEGORIES) {
      const card = document.createElement("div");
      card.className = "categoryCard";
      card.id = cat.catId;

      const header = document.createElement("div");
      header.className = "categoryHeader";
      header.textContent = cat.title;
      const badge = document.createElement("span");
      badge.className = "categoryBadge";
      header.appendChild(badge);
      card.appendChild(header);

      const body = document.createElement("div");
      body.className = "categoryBody";
      trackedCategories.push({ card, badge, from: trackedButtons.length });

      if (cat.catId === "catAudio") {
        const visualizerWrap = document.createElement("div");
        visualizerWrap.className = "audioVisualizerContainer";
        audioWaveCanvas = document.createElement("canvas");
        audioWaveCanvas.className = "audioWaveCanvas";
        audioWaveCanvas.width = 320;
        audioWaveCanvas.height = 70;
        audioWaveCtx = audioWaveCanvas.getContext("2d");

        // Noise Gate Cutoff Slider inside the visualizer box
        const gateWrap = document.createElement("div");
        gateWrap.className = "audioGateWrap";
        const gateLabel = document.createElement("span");
        gateLabel.className = "audioGateLabel";
        gateLabel.textContent = "Noise-Gate:";
        gateLabel.title = "Schneidet leises Hintergrundrauschen/Noise ab (0.00 = offen .. 0.40 = stark)";

        const gateInput = document.createElement("input");
        gateInput.type = "range";
        gateInput.className = "audioGateInput";
        gateInput.min = "0";
        gateInput.max = "0.40";
        gateInput.step = "0.01";
        gateInput.value = String(state.audioNoiseGate ?? 0.04);

        const gateVal = document.createElement("span");
        gateVal.className = "audioGateVal";
        gateVal.textContent = Number(state.audioNoiseGate ?? 0.04).toFixed(2);

        gateInput.addEventListener("input", () => {
          state.audioNoiseGate = parseFloat(gateInput.value);
          gateVal.textContent = Number(state.audioNoiseGate).toFixed(2);
        });

        gateWrap.appendChild(gateLabel);
        gateWrap.appendChild(gateInput);
        gateWrap.appendChild(gateVal);

        visualizerWrap.appendChild(audioWaveCanvas);
        visualizerWrap.appendChild(gateWrap);
        body.appendChild(visualizerWrap);

        trackedSliders.push({ input: gateInput, valEl: gateVal, field: "audioNoiseGate", fmt: (v) => Number(v).toFixed(2) });
      }

      for (const section of cat.sections) {
        const row = document.createElement("div");
        row.className = "controlSection";

        const label = document.createElement("span");
        label.className = "controlSectionLabel";
        label.textContent = section.title;
        row.appendChild(label);

        for (const item of section.items) {
          if (item.kind === "checkboxToggle") {
            const checkboxLabel = document.createElement("label");
            checkboxLabel.className = "effectCheckboxLabel";
            if (item.title) checkboxLabel.title = item.title;

            const input = document.createElement("input");
            input.type = "checkbox";
            input.checked = !!state[item.field];
            input.addEventListener("change", () => {
              state[item.field] = input.checked;
              state.lastToggledEffect = item.name;
            });

            const text = document.createElement("span");
            text.textContent = item.label;
            checkboxLabel.appendChild(input);
            checkboxLabel.appendChild(text);
            row.appendChild(checkboxLabel);
            trackedButtons.push({ el: checkboxLabel, input, kind: "checkboxToggle", field: item.field });
            continue;
          }

          if (item.kind === "pairedToggleSlider") {
            const pairWrap = document.createElement("div");
            pairWrap.className = "pairedToggleSliderWrap";

            // Toggle Button
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "ctrlBtn";
            if (item.toggle.classExtra) btn.classList.add(item.toggle.classExtra);
            btn.textContent = item.toggle.label;
            btn.addEventListener("click", () => toggleEffect(item.toggle.name));
            trackedButtons.push({ el: btn, kind: "toggle", field: item.toggle.field });

            // Slider
            const wrap = document.createElement("div");
            wrap.className = "inlineSliderWrap";
            const name = document.createElement("span");
            name.className = "inlineSliderLabel";
            name.textContent = item.slider.label;

            const input = document.createElement("input");
            input.type = "range";
            input.className = "inlineSliderInput";
            input.min = item.slider.min;
            input.max = item.slider.max;
            input.step = item.slider.step;
            input.value = state[item.slider.field];

            const valEl = document.createElement("span");
            valEl.className = "inlineSliderVal";
            const fmtVal = (v) => Number(v).toFixed(2);
            valEl.textContent = fmtVal(state[item.slider.field]);

            const onSliderChange = () => {
              state[item.slider.field] = parseFloat(input.value);
              valEl.textContent = fmtVal(state[item.slider.field]);
            };

            input.addEventListener("input", onSliderChange);
            input.addEventListener("change", onSliderChange);
            input.addEventListener("pointerdown", (e) => e.stopPropagation());
            wrap.addEventListener("pointerdown", (e) => e.stopPropagation());

            wrap.appendChild(name);
            wrap.appendChild(input);
            wrap.appendChild(valEl);

            pairWrap.appendChild(btn);
            pairWrap.appendChild(wrap);
            row.appendChild(pairWrap);

            trackedSliders.push({ input, valEl, field: item.slider.field, fmt: fmtVal });
            continue;
          }

          if (item.kind === "sliderInline") {
            const wrap = document.createElement("div");
            wrap.className = "inlineSliderWrap";
            const name = document.createElement("span");
            name.className = "inlineSliderLabel";
            name.textContent = item.label;

            const input = document.createElement("input");
            input.type = "range";
            input.className = "inlineSliderInput";
            input.min = item.min;
            input.max = item.max;
            input.step = item.step;
            input.value = state[item.field];

            const valEl = document.createElement("span");
            valEl.className = "inlineSliderVal";
            const fmtVal = (v) => Number(v).toFixed(2);
            valEl.textContent = fmtVal(state[item.field]);

            const onSliderChange = () => {
              state[item.field] = parseFloat(input.value);
              valEl.textContent = fmtVal(state[item.field]);
            };

            input.addEventListener("input", onSliderChange);
            input.addEventListener("change", onSliderChange);
            input.addEventListener("pointerdown", (e) => e.stopPropagation());
            wrap.addEventListener("pointerdown", (e) => e.stopPropagation());

            wrap.appendChild(name);
            wrap.appendChild(input);
            wrap.appendChild(valEl);
            row.appendChild(wrap);

            trackedSliders.push({ input, valEl, field: item.field, fmt: fmtVal });
            continue;
          }

          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "ctrlBtn";
          if (item.classExtra) btn.classList.add(item.classExtra);
          btn.textContent = item.label;

          if (item.kind === "toggle") {
            btn.addEventListener("click", () => toggleEffect(item.name));
            trackedButtons.push({ el: btn, kind: "toggle", field: item.field });
          } else if (item.kind === "held") {
            const press = (e) => {
              e.preventDefault();
              state[item.field] = true;
            };
            const release = () => {
              state[item.field] = false;
            };
            btn.addEventListener("pointerdown", press);
            btn.addEventListener("pointerup", release);
            btn.addEventListener("pointerleave", release);
            btn.addEventListener("pointercancel", release);
            trackedButtons.push({ el: btn, kind: "held", field: item.field });
          } else if (item.kind === "liveMode") {
            btn.addEventListener("click", () => onLiveModeToggle?.());
            trackedButtons.push({ el: btn, kind: "toggle", field: "liveModeOn" });
          } else if (item.kind === "openOutput") {
            btn.addEventListener("click", () => onOpenOutput?.());
          } else if (item.kind === "pip") {
            btn.addEventListener("click", () => onPipToggle?.());
          } else if (item.kind === "fullscreen") {
            btn.addEventListener("click", () => onFullscreenToggle?.());
          } else if (item.kind === "resetCamera") {
            btn.addEventListener("click", () => onResetCamera?.());
          } else if (item.kind === "toggleViewport") {
            btn.addEventListener("click", () => {
              toggleEffect("viewportTopMode");
              window.dispatchEvent(new Event("resize"));
            });
            trackedButtons.push({ el: btn, kind: "toggle", field: "viewportTopMode" });
          } else if (item.kind === "toggleMute") {
            btn.addEventListener("click", () => {
              toggleEffect("muteLocalRender");
            });
            trackedButtons.push({ el: btn, kind: "toggle", field: "muteLocalRender" });
          } else if (item.kind === "tapBpm") {
            btn.addEventListener("click", () => {
              if (onOneshot) onOneshot("tapBpm");
              btn.classList.add("active");
              setTimeout(() => btn.classList.remove("active"), 120);
            });
            const updateBpm = () => {
              const bpmVal = state.tapBpmValue || audio?.bpm || 120;
              btn.textContent = `🥁 Tap BPM (${bpmVal})`;
            };
            trackedButtons.push({ el: btn, kind: "tapBpm", update: updateBpm });
          } else if (item.kind === "blackout") {
            btn.classList.add("blackoutBtn");
            btn.addEventListener("click", () => {
              state.blackoutActive = !state.blackoutActive;
              btn.classList.toggle("active", !!state.blackoutActive);
            });
            trackedButtons.push({ el: btn, kind: "toggle", field: "blackoutActive" });
          } else if (item.kind === "cameraFlightSelect") {
            const flightWrap = document.createElement("div");
            flightWrap.className = "inlineSelectWrap";
            const flightLabel = document.createElement("span");
            flightLabel.className = "inlineSelectLabel";
            flightLabel.textContent = "Kamera-Flug:";

            const flightSelect = document.createElement("select");
            flightSelect.className = "formatSelect inlineFlightSelect";
            const flightModes = [
              { id: "none", label: "Aus (Manuell / Orbit)" },
              { id: "orbit", label: "360° Orbit Flug" },
              { id: "spiral", label: "Aufsteigende Spirale" },
              { id: "figure8", label: "Achter-Schleife (∞)" },
              { id: "tunnelFly", label: "Tunnel-Durchflug" },
            ];
            for (const fm of flightModes) {
              const opt = document.createElement("option");
              opt.value = fm.id;
              opt.textContent = fm.label;
              if ((state.cameraFlightMode || "none") === fm.id) opt.selected = true;
              flightSelect.appendChild(opt);
            }
            flightSelect.addEventListener("change", () => {
              state.cameraFlightMode = flightSelect.value;
            });
            flightWrap.appendChild(flightLabel);
            flightWrap.appendChild(flightSelect);
            row.appendChild(flightWrap);
            continue;
          } else if (item.kind === "exportPreset") {
            btn.addEventListener("click", () => exportPresetJSON());
          } else if (item.kind === "importPreset") {
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ".json";
            fileInput.style.display = "none";
            fileInput.addEventListener("change", async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const success = importPresetJSON(text);
                if (success) {
                  btn.textContent = `✓ ${file.name.slice(0, 10)}`;
                  setTimeout(() => {
                    btn.textContent = item.label;
                  }, 2000);
                }
              } catch (err) {
                alert("Fehler beim Lesen der Datei: " + err.message);
              }
            });
            btn.appendChild(fileInput);
            btn.addEventListener("click", () => fileInput.click());
          } else if (item.kind === "oneshot") {
            btn.addEventListener("click", () => {
              switch (item.action) {
                case "sizeCycle":
                  state.sizeCyclePhase = (state.sizeCyclePhase + 1) % 4;
                  break;
                case "explosion":
                  onOneshot ? onOneshot("explosion") : particleSystem.triggerExplosion(90);
                  break;
                case "rbBurst":
                  onOneshot ? onOneshot("rbBurst") : particleSystem.triggerRbBurst(90);
                  break;
                case "shockwave":
                  onOneshot ? onOneshot("shockwave") : particleSystem.triggerShockwave(180);
                  break;
                case "reset":
                  if (onOneshot) {
                    onOneshot("resetAll");
                  } else {
                    resetEffects();
                    particleSystem?.resetSwarm();
                  }
                  break;
                default:
                  break;
              }
            });
          } else if (item.kind === "morph") {
            btn.addEventListener("click", () => {
              toggleEffect("morph");
              onMorphToggle?.(state.morphOn);
            });
            trackedButtons.push({ el: btn, kind: "morph" });
          } else if (item.kind === "objMorph") {
            btn.addEventListener("click", () => {
              toggleEffect("objMorph");
            });
            trackedButtons.push({ el: btn, kind: "objMorph" });
          } else if (item.kind === "brandMorph") {
            btn.addEventListener("click", () => {
              toggleEffect("brandMorph");
            });
            trackedButtons.push({ el: btn, kind: "brandMorph" });
          } else if (item.kind === "customTextMorph") {
            const customWrap = document.createElement("div");
            customWrap.className = "customMorphWrap";

            const customInput = document.createElement("input");
            customInput.type = "text";
            customInput.maxLength = 10;
            customInput.className = "customMorphInput";
            customInput.placeholder = t("customWordPlaceholder") || "Text (max. 10)...";

            const customBtn = document.createElement("button");
            customBtn.type = "button";
            customBtn.className = "ctrlBtn customMorphBtn";
            customBtn.textContent = t("btnCustomMorph") || "✦ Morph Text";

            const updateCustomBtnState = () => {
              const isCustomActive = state.morphOn && !state.objMorphOn && !state.brandMorphOn;
              customBtn.classList.toggle("active", isCustomActive);
              if (isCustomActive && state.currentCustomText) {
                customBtn.textContent = `${t("btnCustomMorphOff") || "✕ Text Aus"} (${state.currentCustomText})`;
              } else if (isCustomActive) {
                customBtn.textContent = t("btnCustomMorphOff") || "✕ Text Aus";
              } else {
                customBtn.textContent = t("btnCustomMorph") || "✦ Morph Text";
              }
            };

            const triggerCustomMorph = async () => {
              const txt = customInput.value.trim();
              const isCustomActive = state.morphOn && !state.objMorphOn && !state.brandMorphOn;
              
              // If already active and user clicks without typing a new different word -> toggle off!
              if (isCustomActive && (!txt || txt.toUpperCase() === state.currentCustomText)) {
                state.morphOn = false;
                state.currentCustomText = null;
                onMorphToggle?.(false);
                updateCustomBtnState();
                return;
              }

              const wordToLoad = txt || state.currentCustomText || "STARCODE";
              customBtn.textContent = "⏳...";
              const cleanWord = wordToLoad.slice(0, 10).toUpperCase();
              state.currentCustomText = cleanWord;
              await particleSystem.loadCustomWord(cleanWord);
              onMorphToggle?.(true);
              updateCustomBtnState();
            };

            customBtn.addEventListener("click", triggerCustomMorph);
            customInput.addEventListener("keydown", (e) => {
              if (e.key === "Enter") triggerCustomMorph();
            });

            customWrap.appendChild(customInput);
            customWrap.appendChild(customBtn);
            row.appendChild(customWrap);
            trackedButtons.push({ el: customBtn, kind: "customMorph", update: updateCustomBtnState });
            continue;
          } else if (item.kind === "presetsCard") {
            const presetWrap = document.createElement("div");
            presetWrap.className = "presetCardWrap";

            const presetSelect = document.createElement("select");
            presetSelect.className = "formatSelect presetSelect";

            function renderPresetOptions() {
              presetSelect.innerHTML = "";
              const groupFactory = document.createElement("optgroup");
              groupFactory.label = t("presetGroupFactory");
              for (const fp of FACTORY_PRESETS) {
                const opt = document.createElement("option");
                opt.value = "fp:" + fp.id;
                opt.textContent = fp.label;
                groupFactory.appendChild(opt);
              }
              presetSelect.appendChild(groupFactory);

              const userList = loadUserPresets();
              if (userList.length > 0) {
                const groupUser = document.createElement("optgroup");
                groupUser.label = t("presetGroupUser");
                userList.forEach((up, idx) => {
                  const opt = document.createElement("option");
                  opt.value = "up:" + idx;
                  opt.textContent = "★ " + up.name;
                  groupUser.appendChild(opt);
                });
                presetSelect.appendChild(groupUser);
              }
            }

            renderPresetOptions();

            presetSelect.addEventListener("change", () => {
              const val = presetSelect.value;
              if (val.startsWith("fp:")) {
                const id = val.slice(3);
                const fp = FACTORY_PRESETS.find((x) => x.id === id);
                if (fp) applyPreset(fp.state);
              } else if (val.startsWith("up:")) {
                const idx = parseInt(val.slice(3), 10);
                const userList = loadUserPresets();
                if (userList[idx]) applyPreset(userList[idx].state, userList[idx].camera);
              }
            });

            const presetBtnRow = document.createElement("div");
            presetBtnRow.className = "presetBtnRow";

            const savePresetBtn = document.createElement("button");
            savePresetBtn.type = "button";
            savePresetBtn.className = "ctrlBtn presetSaveBtn";
            savePresetBtn.textContent = t("presetSave");
            savePresetBtn.title = t("presetSave");
            savePresetBtn.addEventListener("click", () => {
              const userList = loadUserPresets();
              const name = prompt(t("presetPromptName"), `Preset ${userList.length + 1}`);
              if (!name || !name.trim()) return;
              const currentState = {};
              for (const [key, value] of Object.entries(state)) {
                if (key !== "viewportTopMode" && (typeof value === "boolean" || typeof value === "number")) {
                  currentState[key] = value;
                }
              }
              userList.push({ name: name.trim(), state: currentState, camera: captureCameraState() });
              saveUserPresets(userList);
              renderPresetOptions();
              presetSelect.value = "up:" + (userList.length - 1);
            });

            const deletePresetBtn = document.createElement("button");
            deletePresetBtn.type = "button";
            deletePresetBtn.className = "ctrlBtn presetDeleteBtn";
            deletePresetBtn.textContent = t("presetDelete");
            deletePresetBtn.title = t("presetDelete");
            deletePresetBtn.addEventListener("click", () => {
              const val = presetSelect.value;
              if (!val.startsWith("up:")) {
                alert(t("presetAlertFactory"));
                return;
              }
              const idx = parseInt(val.slice(3), 10);
              const userList = loadUserPresets();
              userList.splice(idx, 1);
              saveUserPresets(userList);
              renderPresetOptions();
              presetSelect.value = "fp:standard";
              const fp = FACTORY_PRESETS[0];
              if (fp) applyPreset(fp.state);
            });

            const randomPresetBtn = document.createElement("button");
            randomPresetBtn.type = "button";
            randomPresetBtn.className = "ctrlBtn presetRandomBtn";
            randomPresetBtn.textContent = t("presetRandom");
            randomPresetBtn.title = "🎲 Random Preset (Zufälliges Preset aktivieren)";
            randomPresetBtn.addEventListener("click", () => {
              const allOptions = Array.from(presetSelect.querySelectorAll("option"));
              if (allOptions.length === 0) return;
              const currentVal = presetSelect.value;
              const otherOptions = allOptions.filter((o) => o.value !== currentVal);
              const targetOpt = otherOptions.length > 0
                ? otherOptions[Math.floor(Math.random() * otherOptions.length)]
                : allOptions[0];
              presetSelect.value = targetOpt.value;
              presetSelect.dispatchEvent(new Event("change"));
            });

            const nextPresetBtn = document.createElement("button");
            nextPresetBtn.type = "button";
            nextPresetBtn.className = "ctrlBtn presetNextBtn";
            nextPresetBtn.textContent = t("presetNext");
            nextPresetBtn.title = "⏭ Next Preset (Nächstes Preset aktivieren)";
            nextPresetBtn.addEventListener("click", () => {
              const allOptions = Array.from(presetSelect.querySelectorAll("option"));
              if (allOptions.length === 0) return;
              const currentIndex = allOptions.findIndex((o) => o.value === presetSelect.value);
              const nextIndex = (currentIndex + 1) % allOptions.length;
              presetSelect.value = allOptions[nextIndex].value;
              presetSelect.dispatchEvent(new Event("change"));
            });

            presetBtnRow.appendChild(savePresetBtn);
            presetBtnRow.appendChild(deletePresetBtn);
            presetBtnRow.appendChild(randomPresetBtn);
            presetBtnRow.appendChild(nextPresetBtn);

            const fileBtnRow = document.createElement("div");
            fileBtnRow.className = "presetBtnRow";
            fileBtnRow.style.marginTop = "0.2rem";

            const exportPresetBtn = document.createElement("button");
            exportPresetBtn.type = "button";
            exportPresetBtn.className = "ctrlBtn presetExportBtn";
            exportPresetBtn.textContent = t("btnExportPreset");
            exportPresetBtn.title = "💾 Preset-Pack / Aktives Preset als JSON exportieren";
            exportPresetBtn.addEventListener("click", () => exportPresetJSON());

            const importPresetBtn = document.createElement("button");
            importPresetBtn.type = "button";
            importPresetBtn.className = "ctrlBtn presetImportBtn";
            importPresetBtn.textContent = t("btnImportPreset");
            importPresetBtn.title = "📂 Preset oder Preset-Pack (.json) importieren";
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ".json";
            fileInput.style.display = "none";
            fileInput.addEventListener("change", async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const success = importPresetJSON(text);
                if (success) {
                  renderPresetOptions();
                  importPresetBtn.textContent = `✓ ${file.name.slice(0, 8)}`;
                  setTimeout(() => {
                    importPresetBtn.textContent = t("btnImportPreset");
                  }, 2000);
                }
              } catch (err) {
                alert("Fehler beim Lesen der Datei: " + err.message);
              }
            });
            importPresetBtn.appendChild(fileInput);
            importPresetBtn.addEventListener("click", () => fileInput.click());

            fileBtnRow.appendChild(exportPresetBtn);
            fileBtnRow.appendChild(importPresetBtn);

            presetWrap.appendChild(presetSelect);
            presetWrap.appendChild(presetBtnRow);
            presetWrap.appendChild(fileBtnRow);
            row.appendChild(presetWrap);
            continue;
          } else if (item.kind === "loadObj") {
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ".obj";
            fileInput.style.display = "none";
            fileInput.addEventListener("change", async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const res = onObjLoaded ? onObjLoaded(text, file.name) : particleSystem.loadCustomObj(text, file.name);
                if (res && res.success !== false) {
                  btn.textContent = `✓ ${file.name.slice(0, 9)}`;
                  state.objMorphOn = true;
                  state.objMorphToggleTime = performance.now() / 1000;
                } else {
                  alert("Fehler beim Laden des OBJ: " + (res?.error || "Ungültiges Format"));
                }
              } catch (err) {
                alert("Fehler beim Lesen der Datei: " + err.message);
              }
            });
            btn.appendChild(fileInput);
            btn.addEventListener("click", () => fileInput.click());
          } else if (item.kind === "permanentColor") {
            btn.addEventListener("click", () => setPermanentColor(item.mode));
            trackedButtons.push({ el: btn, kind: "permanentColor", mode: item.mode });
          }

          row.appendChild(btn);
        }

        body.appendChild(row);
      }

      card.appendChild(body);
      grid.appendChild(card);
      trackedCategories[trackedCategories.length - 1].to = trackedButtons.length;
    }

    container.appendChild(grid);

    // --- Bottom Tools Bar ---
    const bottomBar = document.createElement("div");
    bottomBar.className = "bottomToolsBar";

    // Sliders Box
    const sliderBox = document.createElement("div");
    sliderBox.className = "toolBox sliderBox";
    const sliderHeader = document.createElement("div");
    sliderHeader.className = "toolBoxHeader";
    sliderHeader.textContent = t("boxControls");
    sliderBox.appendChild(sliderHeader);
    makeCollapsible(sliderBox, sliderHeader, "starcode.ui.sliderBox");

    const sliderRow = document.createElement("div");
    sliderRow.className = "sliderRow";

    for (const def of SLIDERS) {
      const wrap = document.createElement("label");
      wrap.className = "ctrlSlider";

      const name = document.createElement("span");
      name.textContent = def.label;

      const input = document.createElement("input");
      input.type = "range";
      input.min = def.min;
      input.max = def.max;
      input.step = def.step;
      input.value = state[def.field];

      const valEl = document.createElement("span");
      valEl.className = "sliderVal";
      const fmtVal = (v) => (def.step >= 1 ? String(Math.round(v)) : Number(v).toFixed(2));
      valEl.textContent = fmtVal(state[def.field]);

      input.addEventListener("input", () => {
        state[def.field] = parseFloat(input.value);
        valEl.textContent = fmtVal(state[def.field]);
      });

      wrap.appendChild(name);
      wrap.appendChild(input);
      wrap.appendChild(valEl);

      if (def.key === "rotation") {
        const rotAudioLabel = document.createElement("label");
        rotAudioLabel.className = "sliderCheckboxWrap";
        rotAudioLabel.title = t("tipRotAudio");
        const rotAudioCheck = document.createElement("input");
        rotAudioCheck.type = "checkbox";
        rotAudioCheck.className = "sliderCheckbox";
        rotAudioCheck.checked = !!state.rotationAudioReact;
        rotAudioCheck.addEventListener("change", () => {
          state.rotationAudioReact = rotAudioCheck.checked;
        });
        const rotAudioText = document.createElement("span");
        rotAudioText.className = "sliderCheckboxText";
        rotAudioText.textContent = t("btnAudioRot");
        rotAudioLabel.appendChild(rotAudioCheck);
        rotAudioLabel.appendChild(rotAudioText);
        wrap.appendChild(rotAudioLabel);
        trackedButtons.push({ el: rotAudioCheck, kind: "checkbox", field: "rotationAudioReact" });

        const spin360Btn = document.createElement("button");
        spin360Btn.type = "button";
        spin360Btn.className = "ctrlBtn spin360Btn";
        spin360Btn.textContent = "🔄 360 Spin";
        spin360Btn.title = "Unbeschränkte 360° Rotation ein-/ausschalten (Taste 3)";
        spin360Btn.addEventListener("click", (e) => {
          e.preventDefault();
          toggleEffect("spin360On");
        });
        wrap.appendChild(spin360Btn);
        trackedButtons.push({ el: spin360Btn, kind: "toggle", field: "spin360On" });
      }

      sliderRow.appendChild(wrap);
      trackedSliders.push({ input, field: def.field, valEl, fmt: fmtVal });
    }

    // Inline Color Spectrum Slider
    const colorWrap = document.createElement("label");
    colorWrap.className = "ctrlSlider ctrlSliderColor";

    const colorName = document.createElement("span");
    colorName.textContent = t("sliderHue");
    colorWrap.appendChild(colorName);

    const barWrap = document.createElement("div");
    barWrap.className = "colorBarWrap";
    barWrap.title = t("tipColorScrub");

    const barTrack = document.createElement("div");
    barTrack.className = "colorBarTrack";

    thumb = document.createElement("div");
    thumb.className = "colorBarThumb";

    barWrap.appendChild(barTrack);
    barWrap.appendChild(thumb);
    colorWrap.appendChild(barWrap);

    colorStatus = document.createElement("span");
    colorStatus.className = "colorBarStatus";
    colorStatus.textContent = state.colorWheelActive ? `${Math.round(state.hueShift * 360)}°` : t("colorOff");
    colorWrap.appendChild(colorStatus);

    colorResetBtn = document.createElement("button");
    colorResetBtn.type = "button";
    colorResetBtn.className = "ctrlBtn colorResetBtn";
    colorResetBtn.textContent = "✕";
    colorResetBtn.title = t("tipColorReset");
    colorResetBtn.addEventListener("click", (e) => {
      e.preventDefault();
      state.colorWheelActive = false;
      state.hueShift = 0.0;
    });
    colorWrap.appendChild(colorResetBtn);

    function updateHueFromPointer(e) {
      const rect = barTrack.getBoundingClientRect();
      if (rect.width <= 0) return;
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const ratio = x / rect.width;
      state.hueShift = ratio;
      state.colorWheelActive = true;
    }

    let isDraggingColor = false;
    barWrap.addEventListener("pointerdown", (e) => {
      isDraggingColor = true;
      try { barWrap.setPointerCapture(e.pointerId); } catch (_) {}
      updateHueFromPointer(e);
    });
    barWrap.addEventListener("pointermove", (e) => {
      if (isDraggingColor) updateHueFromPointer(e);
    });
    const endDrag = (e) => {
      if (isDraggingColor) {
        isDraggingColor = false;
        try { barWrap.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    };
    barWrap.addEventListener("pointerup", endDrag);
    barWrap.addEventListener("pointercancel", endDrag);

    sliderRow.appendChild(colorWrap);
    sliderBox.appendChild(sliderRow);
    bottomBar.appendChild(sliderBox);

    // --- Dedicated Bokeh & Focus Box (Standard 0.0) ---
    const bokehBox = document.createElement("div");
    bokehBox.className = "toolBox bokehBox";

    const bokehHeader = document.createElement("div");
    bokehHeader.className = "toolBoxHeader";
    bokehHeader.textContent = "📷 Bokeh & Focus (DOF)";
    bokehBox.appendChild(bokehHeader);
    makeCollapsible(bokehBox, bokehHeader, "starcode.ui.bokehBox");

    const bokehContent = document.createElement("div");
    bokehContent.className = "bokehContent";

    // Focus Slider
    const focusWrap = document.createElement("label");
    focusWrap.className = "ctrlSlider";
    const focusLabel = document.createElement("span");
    focusLabel.textContent = t("sliderFocusDistance");
    const focusInput = document.createElement("input");
    focusInput.type = "range";
    focusInput.min = "200";
    focusInput.max = "1600";
    focusInput.step = "10";
    focusInput.value = String(state.focusDistance);
    const focusValEl = document.createElement("span");
    focusValEl.className = "sliderVal";
    focusValEl.textContent = String(Math.round(state.focusDistance));
    focusInput.addEventListener("input", () => {
      state.focusDistance = parseFloat(focusInput.value);
      focusValEl.textContent = String(Math.round(state.focusDistance));
    });
    focusWrap.appendChild(focusLabel);
    focusWrap.appendChild(focusInput);
    focusWrap.appendChild(focusValEl);

    const afBtn = document.createElement("button");
    afBtn.type = "button";
    afBtn.className = "ctrlBtn autoFocusBtn";
    afBtn.title = t("tipAutofocus");
    afBtn.textContent = state.autofocusOn ? t("btnAFActive") : t("btnAF");
    afBtn.addEventListener("click", (e) => {
      e.preventDefault();
      state.autofocusOn = !state.autofocusOn;
      afBtn.textContent = state.autofocusOn ? t("btnAFActive") : t("btnAF");
      afBtn.classList.toggle("active", state.autofocusOn);
    });
    focusWrap.appendChild(afBtn);
    trackedButtons.push({ el: afBtn, kind: "toggle", field: "autofocusOn" });
    trackedSliders.push({ input: focusInput, field: "focusDistance", valEl: focusValEl, fmt: (v) => String(Math.round(v)) });
    bokehContent.appendChild(focusWrap);

    // DOF Blur Slider (Initial 0.0)
    const dofWrap = document.createElement("label");
    dofWrap.className = "ctrlSlider";
    const dofLabel = document.createElement("span");
    dofLabel.textContent = t("sliderDofBlur");
    const dofInput = document.createElement("input");
    dofInput.type = "range";
    dofInput.min = "0";
    dofInput.max = "2";
    dofInput.step = "0.01";
    dofInput.value = String(state.dofBlur);
    const dofValEl = document.createElement("span");
    dofValEl.className = "sliderVal";
    dofValEl.textContent = Number(state.dofBlur).toFixed(2);
    dofInput.addEventListener("input", () => {
      state.dofBlur = parseFloat(dofInput.value);
      dofValEl.textContent = Number(state.dofBlur).toFixed(2);
    });
    dofWrap.appendChild(dofLabel);
    dofWrap.appendChild(dofInput);
    dofWrap.appendChild(dofValEl);

    const invertBtn = document.createElement("button");
    invertBtn.type = "button";
    invertBtn.className = "ctrlBtn dofInvertBtn";
    invertBtn.title = t("tipDofInvert");
    invertBtn.textContent = state.dofInvert ? t("btnNearBokeh") : t("btnFarBokeh");
    invertBtn.addEventListener("click", (e) => {
      e.preventDefault();
      state.dofInvert = !state.dofInvert;
      invertBtn.textContent = state.dofInvert ? t("btnNearBokeh") : t("btnFarBokeh");
      invertBtn.classList.toggle("active", state.dofInvert);
    });
    dofWrap.appendChild(invertBtn);
    trackedButtons.push({ el: invertBtn, kind: "toggle", field: "dofInvert" });

    const bokehAudioBtn = document.createElement("button");
    bokehAudioBtn.type = "button";
    bokehAudioBtn.className = "ctrlBtn bokehAudioBtn";
    bokehAudioBtn.title = t("tipBokehAudio");
    bokehAudioBtn.textContent = state.dofAudioReact ? t("btnAudioBokehActive") : t("btnAudioBokeh");
    bokehAudioBtn.addEventListener("click", (e) => {
      e.preventDefault();
      state.dofAudioReact = !state.dofAudioReact;
      bokehAudioBtn.textContent = state.dofAudioReact ? t("btnAudioBokehActive") : t("btnAudioBokeh");
      bokehAudioBtn.classList.toggle("active", state.dofAudioReact);
    });
    dofWrap.appendChild(bokehAudioBtn);
    trackedButtons.push({ el: bokehAudioBtn, kind: "toggle", field: "dofAudioReact" });
    trackedSliders.push({ input: dofInput, field: "dofBlur", valEl: dofValEl, fmt: (v) => Number(v).toFixed(2) });
    bokehContent.appendChild(dofWrap);

    bokehBox.appendChild(bokehContent);
    bottomBar.appendChild(bokehBox);

    // Format Box
    const formatBox = document.createElement("div");
    formatBox.className = "toolBox formatBox";

    const formatHeader = document.createElement("div");
    formatHeader.className = "toolBoxHeader";
    formatHeader.textContent = t("boxFormat");
    formatBox.appendChild(formatHeader);
    makeCollapsible(formatBox, formatHeader, "starcode.ui.formatBox");

    const formatContent = document.createElement("div");
    formatContent.className = "formatContent";

    formatSelect = document.createElement("select");
    formatSelect.className = "formatSelect";
    const isEn = getLang() === "en";
    const PRESETS = [
      { label: isEn ? "16:9 Widescreen (1920 × 1080) — Full HD" : "16:9 Breitbild (1920 × 1080) — Full HD", w: 1920, h: 1080, id: "16:9_1080" },
      { label: isEn ? "16:9 Widescreen (1280 × 720) — HD" : "16:9 Breitbild (1280 × 720) — HD", w: 1280, h: 720, id: "16:9_720" },
      { label: isEn ? "16:9 Widescreen (3840 × 2160) — 4K UHD" : "16:9 Breitbild (3840 × 2160) — 4K UHD", w: 3840, h: 2160, id: "16:9_4k" },
      { label: "16:10 (1920 × 1200)", w: 1920, h: 1200, id: "16:10" },
      { label: "21:9 Ultra-Wide (2560 × 1080)", w: 2560, h: 1080, id: "21:9" },
      { label: isEn ? "4:3 Classic (1440 × 1080)" : "4:3 Klassisch (1440 × 1080)", w: 1440, h: 1080, id: "4:3" },
      { label: isEn ? "1:1 Square (1080 × 1080)" : "1:1 Quadrat (1080 × 1080)", w: 1080, h: 1080, id: "1:1" },
      { label: isEn ? "9:16 Vertical / Social (1080 × 1920)" : "9:16 Vertikal / Social (1080 × 1920)", w: 1080, h: 1920, id: "9:16" },
      { label: isEn ? "Custom…" : "Benutzerdefiniert…", w: state.outputWidth || 1920, h: state.outputHeight || 1080, id: "custom" },
    ];

    for (const p of PRESETS) {
      const opt = document.createElement("option");
      opt.value = p.id;
      opt.textContent = p.label;
      if (p.id === (state.outputPreset || "16:9_1080")) opt.selected = true;
      formatSelect.appendChild(opt);
    }

    const formatInputs = document.createElement("div");
    formatInputs.className = "formatInputs";

    const inputWWrap = document.createElement("label");
    inputWWrap.className = "formatInputWrap";
    inputWWrap.innerHTML = `<span class="formatInputLabel">${t("optWidth")}</span>`;
    inputW = document.createElement("input");
    inputW.type = "number";
    inputW.className = "formatNumInput";
    inputW.min = "320";
    inputW.max = "7680";
    inputW.step = "10";
    inputW.value = state.outputWidth || 1920;
    inputWWrap.appendChild(inputW);
    inputWWrap.appendChild(document.createTextNode("px"));

    const inputHWrap = document.createElement("label");
    inputHWrap.className = "formatInputWrap";
    inputHWrap.innerHTML = `<span class="formatInputLabel">${t("optHeight")}</span>`;
    inputH = document.createElement("input");
    inputH.type = "number";
    inputH.className = "formatNumInput";
    inputH.min = "240";
    inputH.max = "4320";
    inputH.step = "10";
    inputH.value = state.outputHeight || 1080;
    inputHWrap.appendChild(inputH);
    inputHWrap.appendChild(document.createTextNode("px"));

    formatSelect.addEventListener("change", () => {
      const chosen = PRESETS.find((p) => p.id === formatSelect.value);
      if (chosen && chosen.id !== "custom") {
        state.outputWidth = chosen.w;
        state.outputHeight = chosen.h;
        state.outputPreset = chosen.id;
        inputW.value = chosen.w;
        inputH.value = chosen.h;
      } else {
        state.outputPreset = "custom";
      }
      window.dispatchEvent(new Event("resize"));
    });

    const onCustomChange = () => {
      state.outputWidth = parseInt(inputW.value, 10) || 1920;
      state.outputHeight = parseInt(inputH.value, 10) || 1080;
      state.outputPreset = "custom";
      formatSelect.value = "custom";
      window.dispatchEvent(new Event("resize"));
    };
    inputW.addEventListener("input", onCustomChange);
    inputH.addEventListener("input", onCustomChange);

    formatInputs.appendChild(inputWWrap);
    formatInputs.appendChild(inputHWrap);

    formatContent.appendChild(formatSelect);
    formatContent.appendChild(formatInputs);
    formatBox.appendChild(formatContent);
    bottomBar.appendChild(formatBox);

    container.appendChild(bottomBar);

    // --- Dynamic Resize Handle Logic ---
    const resizeHandle = document.getElementById("hudResizeHandle");
    if (resizeHandle) {
      let dragging = false;
      let startY = 0;
      let startHeight = 0;

      const onPointerDown = (e) => {
        dragging = true;
        startY = e.clientY;
        startHeight = container.getBoundingClientRect().height;
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
      };

      const onPointerMove = (e) => {
        if (!dragging) return;
        const dy = startY - e.clientY;
        const newHeight = Math.max(80, Math.min(window.innerHeight * 0.85, startHeight + dy));
        container.style.maxHeight = `${newHeight}px`;
        container.style.height = `${newHeight}px`;
        window.dispatchEvent(new Event("resize"));
      };

      const onPointerUp = () => {
        dragging = false;
        document.body.style.userSelect = "";
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.dispatchEvent(new Event("resize"));
      };

      resizeHandle.addEventListener("pointerdown", onPointerDown);

      resizeHandle.addEventListener("dblclick", () => {
        if (container.style.height === "80px") {
          container.style.height = "auto";
          container.style.maxHeight = "38vh";
        } else {
          container.style.height = "80px";
          container.style.maxHeight = "80px";
        }
        window.dispatchEvent(new Event("resize"));
      });
    }

    // --- MIDI Learn row ---
    if (midi) {
      const midiRow = document.createElement("div");
      midiRow.className = "controlSection midiSection";
      const midiLabel = document.createElement("span");
      midiLabel.className = "controlSectionLabel";
      midiLabel.textContent = t("boxMidi");
      midiRow.appendChild(midiLabel);
      makeCollapsible(midiRow, midiLabel, "starcode.ui.midiRow", true);

      for (const [key, target] of Object.entries(MIDI_TARGETS)) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ctrlBtn";
        btn.addEventListener("click", () => {
          midi.armLearn(midi.learnTarget === key ? null : key);
        });
        midiRow.appendChild(btn);
        trackedLearnButtons.push({ el: btn, key, label: target.label });
      }
      container.appendChild(midiRow);
    }
  }

  renderBar();

  window.addEventListener("starcode:langchange", () => {
    renderBar();
  });

  function drawAudioVisualizer() {
    if (!audioWaveCtx || !audioWaveCanvas || !audio) return;
    const ctx = audioWaveCtx;
    const w = audioWaveCanvas.width;
    const h = audioWaveCanvas.height;
    ctx.clearRect(0, 0, w, h);

    const midY = h / 2;
    const numBars = 36;
    const bins = audio.getSpectrumBins ? audio.getSpectrumBins(numBars) : new Float32Array(numBars);
    const barW = (w - (numBars - 1) * 2) / numBars;
    const maxBarH = h * 0.42;

    // 1. Draw glowing gradient spectrum mirror bars
    for (let i = 0; i < numBars; i++) {
      const x = i * (barW + 2);
      const ratio = i / (numBars - 1);
      const energy = bins[i] || 0.05;
      const barH = Math.max(2, energy * maxBarH * (1.0 + (audio.beatPulse || 0) * 0.4));

      const grad = ctx.createLinearGradient(0, midY - barH, 0, midY + barH);
      if (ratio < 0.22) {
        grad.addColorStop(0, "rgba(245, 158, 11, 0.85)");
        grad.addColorStop(0.5, "rgba(239, 68, 68, 0.95)");
        grad.addColorStop(1, "rgba(245, 158, 11, 0.85)");
      } else if (ratio < 0.48) {
        grad.addColorStop(0, "rgba(236, 72, 153, 0.85)");
        grad.addColorStop(0.5, "rgba(244, 63, 94, 0.95)");
        grad.addColorStop(1, "rgba(236, 72, 153, 0.85)");
      } else if (ratio < 0.72) {
        grad.addColorStop(0, "rgba(16, 185, 129, 0.85)");
        grad.addColorStop(0.5, "rgba(34, 197, 94, 0.95)");
        grad.addColorStop(1, "rgba(16, 185, 129, 0.85)");
      } else if (ratio < 0.88) {
        grad.addColorStop(0, "rgba(6, 182, 212, 0.85)");
        grad.addColorStop(0.5, "rgba(59, 130, 246, 0.95)");
        grad.addColorStop(1, "rgba(6, 182, 212, 0.85)");
      } else {
        grad.addColorStop(0, "rgba(168, 85, 247, 0.85)");
        grad.addColorStop(0.5, "rgba(139, 92, 246, 0.95)");
        grad.addColorStop(1, "rgba(168, 85, 247, 0.85)");
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x, midY - barH, barW, barH * 2, 2);
      } else {
        ctx.rect(x, midY - barH, barW, barH * 2);
      }
      ctx.fill();
    }

    // 2. Smooth outline wave curves on top & bottom
    ctx.beginPath();
    ctx.moveTo(0, midY);
    for (let i = 0; i < numBars; i++) {
      const x = i * (barW + 2) + barW / 2;
      const energy = bins[i] || 0.05;
      const barH = energy * maxBarH * (1.0 + (audio.beatPulse || 0) * 0.4);
      ctx.lineTo(x, midY - barH);
    }
    ctx.lineTo(w, midY);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.45)";
    ctx.lineWidth = 1.2;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, midY);
    for (let i = 0; i < numBars; i++) {
      const x = i * (barW + 2) + barW / 2;
      const energy = bins[i] || 0.05;
      const barH = energy * maxBarH * (1.0 + (audio.beatPulse || 0) * 0.4);
      ctx.lineTo(x, midY + barH);
    }
    ctx.lineTo(w, midY);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.28)";
    ctx.lineWidth = 1.0;
    ctx.stroke();

    // 3. Central baseline with beat pulse glow
    ctx.beginPath();
    ctx.moveTo(0, midY);
    ctx.lineTo(w, midY);
    ctx.strokeStyle = audio.beat ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.4)";
    ctx.lineWidth = audio.beat ? 1.8 : 1.0;
    ctx.stroke();

    // 4. Noise Gate Threshold Indicator Lines (Upper & Lower Cutoff Marker)
    const gateRatio = Math.min(1.0, (state.audioNoiseGate ?? 0.04) / 0.40);
    const gateH = gateRatio * maxBarH;
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.moveTo(0, midY - gateH);
    ctx.lineTo(w, midY - gateH);
    ctx.moveTo(0, midY + gateH);
    ctx.lineTo(w, midY + gateH);
    ctx.strokeStyle = "rgba(244, 63, 94, 0.65)";
    ctx.lineWidth = 1.0;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Called every frame from main.js's animate loop so buttons stay visually in sync
  return function syncControlBar() {
    drawAudioVisualizer();

    for (const tracked of trackedButtons) {
      let active = false;
      if (tracked.kind === "checkbox") {
        active = !!state[tracked.field];
        if (tracked.el.checked !== active) tracked.el.checked = active;
      } else if (tracked.kind === "checkboxToggle") {
        active = !!state[tracked.field];
        if (tracked.input.checked !== active) tracked.input.checked = active;
      } else if (tracked.kind === "toggle" || tracked.kind === "held") {
        active = !!state[tracked.field];
      } else if (tracked.kind === "morph") {
        active = state.morphOn;
      } else if (tracked.kind === "objMorph") {
        active = state.objMorphOn;
      } else if (tracked.kind === "brandMorph") {
        active = state.brandMorphOn;
      } else if (tracked.kind === "permanentColor") {
        active = state.permanentColor === tracked.mode;
      } else if (tracked.kind === "customMorph") {
        tracked.update?.();
      }
      if (tracked.kind !== "checkbox") {
        tracked.el.classList.toggle("active", active);
      }
      tracked.isActive = active;
      if (tracked.field === "dofInvert") {
        tracked.el.textContent = active ? t("btnNearBokeh") : t("btnFarBokeh");
      }
      if (tracked.field === "dofAudioReact") {
        tracked.el.textContent = active ? t("btnAudioBokehActive") : t("btnAudioBokeh");
      }
      if (tracked.field === "autofocusOn") {
        tracked.el.textContent = active ? t("btnAFActive") : t("btnAF");
      }
    }

    // Per-category counter
    for (const cat of trackedCategories) {
      let n = 0;
      for (let i = cat.from; i < cat.to; i++) if (trackedButtons[i].isActive) n++;
      const text = n > 0 ? String(n) : "";
      if (cat.badge.textContent !== text) cat.badge.textContent = text;
      cat.badge.classList.toggle("on", n > 0);
    }

    // Sliders
    for (const s of trackedSliders) {
      const v = state[s.field] ?? 0;
      if (s.valEl) {
        const txt = s.fmt(v);
        if (s.valEl.textContent !== txt) s.valEl.textContent = txt;
      }
      if (document.activeElement === s.input || s.input.matches(":active")) continue;
      if (Math.abs(parseFloat(s.input.value) - v) > 0.001) s.input.value = v;
    }

    for (const lb of trackedLearnButtons) {
      const arming = midi?.learnTarget === lb.key;
      const cc = midi?.ccForTarget(lb.key);
      lb.el.textContent = arming
        ? `${lb.label}: ${getLang() === "en" ? "Move knob..." : "Regler bewegen…"}`
        : cc != null
          ? `${lb.label} (CC${cc})`
          : `${lb.label} —`;
      lb.el.classList.toggle("active", arming);
    }

    // Format inputs
    if (inputW && document.activeElement !== inputW && state.outputWidth && String(state.outputWidth) !== inputW.value) {
      inputW.value = state.outputWidth;
    }
    if (inputH && document.activeElement !== inputH && state.outputHeight && String(state.outputHeight) !== inputH.value) {
      inputH.value = state.outputHeight;
    }
    if (formatSelect && document.activeElement !== formatSelect && state.outputPreset && formatSelect.value !== state.outputPreset) {
      formatSelect.value = state.outputPreset;
    }

    // Color status update
    const isColorActive = state.colorWheelActive || state.hueShift > 0.001;
    if (thumb && colorStatus && colorResetBtn) {
      if (isColorActive) {
        const deg = Math.round(state.hueShift * 360);
        colorStatus.textContent = `${deg}°`;
        thumb.style.left = `${(state.hueShift * 100).toFixed(1)}%`;
        thumb.style.background = `hsl(${deg}, 95%, 55%)`;
        thumb.style.boxShadow = `0 0 10px hsl(${deg}, 95%, 55%)`;
        thumb.style.opacity = "1";
        colorResetBtn.classList.remove("hidden");
      } else {
        colorStatus.textContent = t("colorOff");
        thumb.style.left = "0%";
        thumb.style.background = "#ffffff";
        thumb.style.boxShadow = "none";
        thumb.style.opacity = "0";
        colorResetBtn.classList.add("hidden");
      }
    }
  };
}
